import { useEffect, useState } from "react";
import api, { billingErrorMessage } from "@/lib/api";
import { useProjects } from "@/contexts/ProjectContext";
import DashboardLayout from "@/components/dashboard/DashboardLayout";
import PageHeader from "@/components/dashboard/PageHeader";
import { toast } from "sonner";
import { GooglePropertyPicker } from "@/components/integrations/GooglePropertyPicker";

const STATUS_LABEL = {
  connected: { label: "Connected", color: "text-emerald-600 border-emerald-500/40" },
  syncing: { label: "Syncing", color: "text-blue-600 border-blue-500/40" },
  emergent_universal_key: { label: "Universal LLM key", color: "text-gold-600 border-gold-500/40" },
  configuration_required: { label: "Configuration required", color: "text-amber-600 border-amber-500/40" },
  placeholder: { label: "Placeholder", color: "text-muted-foreground border-border" },
  not_connected: { label: "Not connected", color: "text-muted-foreground border-border" },
  error: { label: "Error", color: "text-red-600 border-red-500/40" },
  reconnect_required: { label: "Reconnect required", color: "text-red-600 border-red-500/40" },
  selection_required: { label: "Select property", color: "text-blue-600 border-blue-500/40" },
};

const GOOGLE_KEYS = ["google_search_console", "google_analytics"];

function labelFor(s) {
  return STATUS_LABEL[s] || { label: s, color: "text-muted-foreground border-border" };
}

export default function IntegrationsPage() {
  const { activeProjectId } = useProjects();
  const [rows, setRows] = useState([]);
  const [shopifyStatus, setShopifyStatus] = useState(null);
  const [showConnect, setShowConnect] = useState(false);
  const [shopInput, setShopInput] = useState("");
  const [connecting, setConnecting] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [googleConnecting, setGoogleConnecting] = useState(false);
  const [googlePicker, setGooglePicker] = useState(null); // "gsc" | "ga4" | null

  // One Google OAuth grant covers both Search Console and GA4 (same two read-only scopes).
  const startGoogleConnect = async () => {
    if (!activeProjectId) return toast.error("Select a project first");
    setGoogleConnecting(true);
    try {
      const { data } = await api.get("/oauth/google/start", { params: { project_id: activeProjectId } });
      if (!data?.authorization_url) throw new Error("no_url");
      window.location.assign(data.authorization_url);
    } catch (err) {
      const detail = err?.response?.data?.detail;
      const code = typeof detail === "object" && detail ? detail.code : null;
      if (code === "not_configured") {
        toast.error("Google OAuth is not configured on the server. Add GOOGLE_OAUTH_CLIENT_ID, GOOGLE_OAUTH_CLIENT_SECRET and GOOGLE_OAUTH_REDIRECT_URI in Emergent Secrets.");
      } else if (err?.response?.status === 401) {
        toast.error("Your session expired. Please sign in again.");
      } else {
        toast.error("Could not start Google connection. Please try again.");
      }
      setGoogleConnecting(false);
    }
  };

  const disconnectGoogle = async () => {
    if (!confirm("Disconnect Google? Search Console and GA4 access will be revoked for this project.")) return;
    try {
      await api.delete(`/google/disconnect/${activeProjectId}`);
      toast.success("Google disconnected");
      refresh();
    } catch {
      toast.error("Could not disconnect Google. Please try again.");
    }
  };

  const refresh = async () => {
    if (!activeProjectId) return;
    const { data } = await api.get(`/integrations/${activeProjectId}`);
    setRows(data);
    try {
      const s = await api.get(`/shopify/status/${activeProjectId}`);
      setShopifyStatus(s.data);
    } catch { /* ok */ }
  };
  useEffect(() => {
    refresh();
    // If Shopify OAuth just returned, acknowledge and clean the URL so a refresh
    // doesn't retrigger the toast. Never initiates another OAuth flow.
    try {
      const params = new URLSearchParams(window.location.search);
      const connected = params.get("connected");
      if (connected === "shopify" || connected === "google") {
        const label = connected === "shopify" ? "Shopify" : "Google";
        toast.success(`${label} connected successfully.${connected === "shopify" ? " Initial sync running…" : ""}`);
        params.delete("connected");
        const clean = window.location.pathname + (params.toString() ? `?${params.toString()}` : "");
        window.history.replaceState({}, "", clean);
      }
    } catch { /* ignore */ }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeProjectId]);

  const grouped = rows.reduce((acc, r) => {
    const key = r.category || "Other";
    acc[key] = acc[key] || [];
    acc[key].push(r);
    return acc;
  }, {});

  const startShopifyConnect = async () => {
    if (!shopInput.trim()) return toast.error("Enter your .myshopify.com domain");
    setConnecting(true);
    try {
      const { data } = await api.post("/oauth/shopify/start", {
        project_id: activeProjectId,
        shop_domain: shopInput.trim(),
      });
      window.location.href = data.authorization_url;
    } catch (err) {
      const detail = err?.response?.data?.detail;
      const msg = typeof detail === "string" ? detail : (detail?.message || "Failed to start Shopify OAuth");
      toast.error(msg);
    } finally {
      setConnecting(false);
    }
  };

  const syncNow = async () => {
    setSyncing(true);
    try {
      await api.post(`/shopify/sync/${activeProjectId}`);
      toast.success("Shopify sync complete");
      refresh();
    } catch (err) {
      toast.error(billingErrorMessage(err) || (typeof err?.response?.data?.detail === "string" ? err.response.data.detail : "Sync failed"));
    } finally {
      setSyncing(false);
    }
  };

  const disconnect = async () => {
    if (!confirm("Disconnect this store? Live metrics will stop updating.")) return;
    await api.delete(`/shopify/disconnect/${activeProjectId}`);
    toast.success("Shopify disconnected");
    refresh();
  };

  return (
    <DashboardLayout>
      <PageHeader
        overline="Integrations"
        title="Plug into your store, search, analytics, and comms."
        description="Every integration is labelled honestly. Nothing is presented as active until real credentials are configured and verified."
      />

      {shopifyStatus?.connected && shopifyStatus.shop_name && (
        <div className="bg-emerald-50 border border-emerald-500/40 rounded-sm p-4 mb-6 flex flex-col sm:flex-row sm:items-center gap-3 sm:justify-between" data-testid="shopify-connected-banner">
          <div>
            <p className="editorial-overline">Shopify · Connected</p>
            <p className="text-sm text-emerald-900 mt-1">
              <strong>{shopifyStatus.shop_name}</strong>
              <span className="text-emerald-800/70 ml-2 text-xs font-mono">{shopifyStatus.shop_domain}</span>
            </p>
            <p className="text-xs text-emerald-800/70 mt-1">
              Last sync: {shopifyStatus.last_sync_at ? new Date(shopifyStatus.last_sync_at).toLocaleString() : "pending"} · Scopes: {shopifyStatus.scopes}
            </p>
          </div>
          <div className="flex gap-2">
            <button onClick={syncNow} disabled={syncing} className="px-4 py-2 border border-emerald-600 text-emerald-800 hover:bg-emerald-100 rounded-sm text-sm" data-testid="shopify-sync-now">
              {syncing ? "Syncing…" : "Sync now"}
            </button>
            <button onClick={disconnect} className="px-4 py-2 border border-red-500/50 text-red-700 hover:bg-red-50 rounded-sm text-sm" data-testid="shopify-disconnect">
              Disconnect
            </button>
          </div>
        </div>
      )}

      <div className="space-y-8">
        {Object.entries(grouped).map(([cat, items]) => (
          <section key={cat}>
            <p className="editorial-overline mb-3">{cat}</p>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
              {items.map((r) => {
                const s = labelFor(r.status);
                const isShopify = r.key === "shopify";
                const isGoogle = GOOGLE_KEYS.includes(r.key);
                return (
                  <div key={r.key} className="bg-white border border-border rounded-sm p-5" data-testid={`integration-${r.key}`}>
                    <div className="flex items-center justify-between gap-3">
                      <h3 className="font-serif text-lg">{r.name}</h3>
                      <span className={`text-[10px] uppercase tracking-widest border px-2 py-0.5 rounded-sm ${s.color}`} data-testid={`integration-status-${r.key}`}>
                        {s.label}
                      </span>
                    </div>
                    <p className="text-xs text-muted-foreground mt-3 min-h-[36px]">{r.note || ""}</p>
                    {isShopify && r.status !== "connected" && (
                      <button
                        onClick={() => setShowConnect(true)}
                        className="mt-3 text-xs px-3 py-1.5 bg-navy-900 text-ivory-50 hover:bg-navy-800 rounded-sm"
                        data-testid="connect-shopify-btn"
                        disabled={r.status === "configuration_required"}
                        title={r.status === "configuration_required" ? "SHOPIFY_API_KEY and SHOPIFY_API_SECRET must be set" : "Connect a Shopify store"}
                      >
                        Connect Shopify
                      </button>
                    )}
                    {!isShopify && !isGoogle && (
                      <button className="mt-3 text-xs px-3 py-1.5 border border-border rounded-sm hover:border-gold-500" data-testid={`integration-config-${r.key}`}>
                        Configure
                      </button>
                    )}
                    {isGoogle && (r.status === "not_connected" || r.status === "configuration_required" || r.status === "reconnect_required") && (
                      <button
                        onClick={startGoogleConnect}
                        disabled={googleConnecting}
                        className="mt-3 text-xs px-3 py-1.5 bg-navy-900 text-ivory-50 hover:bg-navy-800 rounded-sm disabled:opacity-50"
                        data-testid={`connect-google-btn-${r.key}`}
                        title="Sign in with Google (read-only Search Console + GA4 access)"
                      >
                        {googleConnecting ? "Redirecting to Google…" : r.status === "reconnect_required" ? "Reconnect Google" : "Connect Google"}
                      </button>
                    )}
                    {isGoogle && r.status === "selection_required" && (
                      <div className="mt-3 space-y-2" data-testid={`google-selection-required-${r.key}`}>
                        <p className="text-xs text-emerald-700">
                          Google account connected · {r.key === "google_search_console" ? "Search Console property" : "GA4 property"} selection required
                        </p>
                        <div className="flex gap-2">
                          <button
                            onClick={() => setGooglePicker(r.key === "google_search_console" ? "gsc" : "ga4")}
                            className="text-xs px-3 py-1.5 bg-navy-900 text-ivory-50 hover:bg-navy-800 rounded-sm"
                            data-testid={`google-select-property-${r.key}`}
                          >
                            Select property
                          </button>
                          <button onClick={disconnectGoogle} className="text-xs px-3 py-1.5 border border-red-500/50 text-red-700 hover:bg-red-50 rounded-sm" data-testid={`google-disconnect-${r.key}`}>
                            Disconnect
                          </button>
                        </div>
                      </div>
                    )}
                    {isGoogle && r.status === "connected" && (
                      <div className="mt-3 flex gap-2">
                        <button
                          onClick={() => setGooglePicker(r.key === "google_search_console" ? "gsc" : "ga4")}
                          className="text-xs px-3 py-1.5 border border-border rounded-sm hover:border-gold-500"
                          data-testid={`google-change-property-${r.key}`}
                        >
                          Change property
                        </button>
                        <button onClick={disconnectGoogle} className="text-xs px-3 py-1.5 border border-red-500/50 text-red-700 hover:bg-red-50 rounded-sm" data-testid={`google-disconnect-${r.key}`}>
                          Disconnect Google
                        </button>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </section>
        ))}
      </div>

      {showConnect && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-40" data-testid="shopify-connect-modal">
          <div className="bg-white rounded-sm border border-border p-6 max-w-md w-full mx-4">
            <p className="editorial-overline">Connect Shopify</p>
            <h3 className="font-serif text-2xl mt-2">Your .myshopify.com store</h3>
            <p className="text-sm text-muted-foreground mt-2">
              Enter your Shopify store domain. You'll be redirected to Shopify to authorize a read-only connection.
            </p>
            <input
              value={shopInput}
              onChange={(e) => setShopInput(e.target.value)}
              placeholder="your-store.myshopify.com"
              data-testid="shopify-shop-input"
              className="mt-4 w-full bg-transparent border border-border px-4 py-3 text-sm rounded-sm focus:border-gold-500 outline-none font-mono"
            />
            <div className="flex justify-end gap-2 mt-5">
              <button onClick={() => setShowConnect(false)} className="px-4 py-2 border border-border rounded-sm text-sm" data-testid="shopify-cancel">
                Cancel
              </button>
              <button onClick={startShopifyConnect} disabled={connecting} className="px-4 py-2 bg-gold-500 hover:bg-gold-400 text-navy-900 font-semibold rounded-sm text-sm disabled:opacity-50" data-testid="shopify-connect-confirm">
                {connecting ? "Redirecting…" : "Connect to Shopify"}
              </button>
            </div>
          </div>
        </div>
      )}
      {googlePicker && (
        <GooglePropertyPicker
          kind={googlePicker}
          projectId={activeProjectId}
          onClose={() => setGooglePicker(null)}
          onAttached={() => { setGooglePicker(null); refresh(); }}
        />
      )}
    </DashboardLayout>
  );
}
